import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  CssBaseline,
  useTheme,
  Button,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Inventory,
  ShoppingCart,
  People,
  Analytics,
  AccountCircle,
  Logout,
  Settings,
  Star,
  Security,
  AccountBalance,
  Assignment,
  MonetizationOn,
  ChevronLeft,
  ChevronRight,
  DarkMode,
  LightMode,
  Handshake,
  Feedback,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LanguageSwitcher from '../LanguageSwitcher';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const logo = require('../logo.png');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const logoBlack = require('../logo_black.png');

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const menuItemDefs = [
  { key: 'dashboard', icon: <Dashboard />, path: '/admin' },
  { key: 'products', icon: <Inventory />, path: '/admin/products' },
  { key: 'orders', icon: <ShoppingCart />, path: '/admin/orders' },
  { key: 'refundManagement', icon: <Assignment />, path: '/admin/refund-management' },
  { key: 'sellerManagement', icon: <People />, path: '/admin/seller-management' },
  { key: 'settlement', icon: <MonetizationOn />, path: '/admin/settlement' },
  { key: 'brandSettlement', icon: <AccountBalance />, path: '/admin/brand-settlement' },
  { key: 'affiliateManagement', icon: <Handshake />, path: '/admin/affiliate-management' },
  { key: 'feedback', icon: <Feedback />, path: '/admin/feedback' },
];

function AdminBadge() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        bgcolor: '#dbeafe',
        color: '#3b82f6',
        fontWeight: 700,
        fontSize: 13,
        px: 1.2,
        py: 0.2,
        borderRadius: 2,
        ml: 1,
      }}
    >
      <Security sx={{ fontSize: 16, mr: 0.3 }} /> Admin
    </Box>
  );
}

function Header({ onLogout, isDarkMode }: { onLogout: () => void; isDarkMode: boolean }) {
  const { t } = useTranslation();
  const [nickname, setNickname] = useState<string>('Admin');
  const [loading, setLoading] = useState(true);

  // 사용자 닉네임 가져오기
  useEffect(() => {
    const fetchUserProfile = async () => {
      console.log('=== Admin 프로필 조회 시작 ===');

      try {
        // 1. 세션 확인 (간단한 방식)
        console.log('세션 조회 시작...');

        const { data: { session } } = await supabase.auth.getSession();
        console.log('세션 확인:', session?.user?.id);

        if (session?.user) {
          // 2. 프로필 조회
          console.log('프로필 조회 시도...');
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('nick_name')
            .eq('id', session.user.id)
            .single();

          console.log('프로필 조회 결과:', profile, error);

          if (profile?.nick_name) {
            console.log('닉네임 설정:', profile.nick_name);
            setNickname(profile.nick_name);
          } else {
            console.log('닉네임이 없음, 기본값 사용');
            setNickname('Admin');
          }
        } else {
          console.log('세션이 없음');
          setNickname('Admin');
        }
      } catch (error) {
        console.error('프로필 조회 실패:', error);
        setNickname('Admin');
      } finally {
        console.log('로딩 완료');
        setLoading(false);
      }
    };

    // 즉시 실행
    fetchUserProfile();

    // 3초 후에도 로딩이 안되면 기본값으로 설정
    const fallbackTimer = setTimeout(() => {
      if (loading) {
        console.log('타임아웃 - 기본값으로 설정');
        setNickname('Admin');
        setLoading(false);
      }
    }, 3000);

    return () => clearTimeout(fallbackTimer);
  }, [loading]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: isDarkMode ? '#1f2937' : '#ffffff',
        borderRadius: 2,
        px: 3,
        py: 1.2,
        minHeight: 68,
        boxSizing: 'border-box',
        boxShadow: isDarkMode ? '0 1px 3px 0 rgba(0, 0, 0, 0.3)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
        mb: 2,
      }}
    >
      {/* Logo Image */}
      <Box>
        <img
          src={isDarkMode ? logoBlack : logo}
          alt="Clozet Logo"
          style={{
            height: 48,
            width: 'auto',
            objectFit: 'contain',
            marginLeft: 2,
            transition: 'opacity 0.3s ease'
          }}
        />
      </Box>
      {/* Profile */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ textAlign: 'right', mr: 1.2 }}>
          <Typography fontWeight={600} fontSize={15} color={isDarkMode ? '#f9fafb' : '#1f2937'}>
            {loading ? t('common.loading') : nickname}
          </Typography>
          <AdminBadge />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
            <LanguageSwitcher isDarkMode={isDarkMode} />
            <Button
              variant="text"
              size="small"
              sx={{
                color: isDarkMode ? '#d1d5db' : '#6b7280',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: 13,
                minWidth: 0,
                p: 0
              }}
              onClick={onLogout}
            >
              {t('common.logout')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function AdminLayout() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [pendingSellersCount, setPendingSellersCount] = useState<number>(0);
  const [unreadFeedbacksCount, setUnreadFeedbacksCount] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleDarkModeToggle = () => {
    setIsDarkMode(!isDarkMode);
    // localStorage에 다크모드 설정 저장
    localStorage.setItem('darkMode', (!isDarkMode).toString());
  };

  // 컴포넌트 마운트 시 다크모드 설정 불러오기
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode) {
      setIsDarkMode(savedDarkMode === 'true');
    }
  }, []);

  // 다크모드 변경 시 document에 테마 속성 설정
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  }, [isDarkMode]);

  // 초기 로드 시 카운트 조회 및 실시간 구독 설정
  useEffect(() => {
    // 초기 카운트 로드
    loadPendingSellersCount();
    loadUnreadFeedbacksCount();

    // seller_profile 테이블 실시간 구독 (status 변경 감지)
    const sellersSubscription = supabase
      .channel('seller-profile-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'seller_profile',
        },
        () => {
          loadPendingSellersCount();
        }
      )
      .subscribe();

    // feedback 테이블 실시간 구독 (is_read 변경 감지)
    const feedbackSubscription = supabase
      .channel('feedback-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'feedback',
        },
        () => {
          loadUnreadFeedbacksCount();
        }
      )
      .subscribe();

    // cleanup
    return () => {
      sellersSubscription.unsubscribe();
      feedbackSubscription.unsubscribe();
    };
  }, []);

  // Pending Sellers 카운트 조회 함수
  const loadPendingSellersCount = async () => {
    try {
      const { count, error } = await supabase
        .from('seller_profile')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (!error && count !== null) {
        setPendingSellersCount(count);
      }
    } catch (error) {
      console.error('Pending sellers count 조회 실패:', error);
    }
  };

  // Unread Feedbacks 카운트 조회 함수
  const loadUnreadFeedbacksCount = async () => {
    try {
      const { count, error } = await supabase
        .from('feedback')
        .select('*', { count: 'exact', head: true })
        .eq('is_read', false);

      if (!error && count !== null) {
        setUnreadFeedbacksCount(count);
      }
    } catch (error) {
      console.error('Unread feedbacks count 조회 실패:', error);
    }
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // 로그아웃 함수: 즉시 이동 방식
  const handleLogout = () => {
    console.log('=== Admin 로그아웃 버튼 클릭됨 ===');

    // 1. 즉시 로컬 세션 정리
    console.log('로컬 세션 즉시 정리 시작...');

    // localStorage 완전 정리
    const localStorageKeys = Object.keys(localStorage);
    localStorageKeys.forEach(key => {
      if (key.includes('supabase') || key.includes('auth') || key.includes('session')) {
        localStorage.removeItem(key);
        console.log('localStorage 삭제:', key);
      }
    });

    // sessionStorage 완전 정리
    const sessionStorageKeys = Object.keys(sessionStorage);
    sessionStorageKeys.forEach(key => {
      if (key.includes('supabase') || key.includes('auth') || key.includes('session')) {
        sessionStorage.removeItem(key);
        console.log('sessionStorage 삭제:', key);
      }
    });

    // 쿠키 정리
    document.cookie.split(";").forEach(function(c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    console.log('로컬 세션 정리 완료');

    // 2. 즉시 로그인 페이지로 이동
    console.log('즉시 로그인 페이지로 이동...');
    window.location.replace('/login');

    // 3. 백그라운드에서 Supabase 로그아웃 처리 (사용자 대기 없음)
    supabase.auth.signOut().then(() => {
      console.log('백그라운드 Supabase 로그아웃 완료');
    }).catch((error) => {
      console.warn('백그라운드 Supabase 로그아웃 실패:', error);
    });
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ minHeight: 20, p: 0, mb: 0 }} />
      {/* Menu 텍스트 추가 */}
      <Box sx={{ px: 2, py: 0.3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {!sidebarCollapsed && (
          <Typography variant="subtitle1" fontWeight={600} color={isDarkMode ? '#f9fafb' : '#1f2937'} sx={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('common.menu')}</Typography>
        )}
        <IconButton
          onClick={handleSidebarToggle}
          sx={{
            color: isDarkMode ? '#ccc' : '#666',
            '&:hover': { color: isDarkMode ? '#fff' : '#000' }
          }}
        >
          {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <List sx={{ flex: 1 }}>
        {menuItemDefs.map((item) => {
          // Badge 카운트 가져오기
          const getBadgeCount = () => {
            if (item.key === 'sellerManagement') return pendingSellersCount;
            if (item.key === 'feedback') return unreadFeedbacksCount;
            return 0;
          };

          const badgeCount = getBadgeCount();
          const badgeContent = badgeCount > 99 ? '99+' : badgeCount;

          return (
            <ListItem key={item.key} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  bgcolor: location.pathname === item.path ? (isDarkMode ? '#374151' : '#f3f4f6') : 'transparent',
                  '&.Mui-selected': {
                    bgcolor: isDarkMode ? '#374151' : '#e5e7eb',
                    color: isDarkMode ? '#f9fafb' : '#1f2937',
                    fontWeight: 600,
                  },
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                }}
              >
                <ListItemIcon sx={{
                  color: location.pathname === item.path ? (isDarkMode ? '#f9fafb' : '#3b82f6') : (isDarkMode ? '#d1d5db' : '#6b7280'),
                  minWidth: sidebarCollapsed ? 24 : 36
                }}>
                  {badgeCount > 0 ? (
                    <Badge
                      badgeContent={badgeContent}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: '#fb923c',
                          color: '#fff',
                          fontSize: '0.65rem',
                          height: '18px',
                          minWidth: '18px',
                          fontWeight: 700,
                        }
                      }}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                {!sidebarCollapsed && (
                  <ListItemText
                    primary={t(`nav.admin.${item.key}`)}
                    sx={{
                      fontWeight: location.pathname === item.path ? 700 : 400,
                      '& .MuiListItemText-primary': {
                        color: isDarkMode ? '#fff' : '#000'
                      }
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* 다크모드 토글 버튼 */}
      <Box sx={{ p: 2, mt: 'auto' }}>
        <ListItemButton
          onClick={handleDarkModeToggle}
          sx={{
            borderRadius: 2,
            bgcolor: 'transparent',
            '&:hover': { bgcolor: isDarkMode ? '#404040' : '#f5f5f5' },
            minHeight: 48,
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          }}
        >
          <ListItemIcon sx={{
            color: isDarkMode ? '#ccc' : '#666',
            minWidth: sidebarCollapsed ? 24 : 36
          }}>
            {isDarkMode ? <LightMode /> : <DarkMode />}
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText
              primary={isDarkMode ? t('common.lightMode') : t('common.darkMode')}
              sx={{
                fontWeight: 400,
                '& .MuiListItemText-primary': {
                  color: isDarkMode ? '#fff' : '#000'
                }
              }}
            />
          )}
        </ListItemButton>
        {sidebarCollapsed && (
          <Box sx={{ mt: 1 }}>
            <LanguageSwitcher isDarkMode={isDarkMode} collapsed />
          </Box>
        )}
        {!sidebarCollapsed && (
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: isDarkMode ? '#555' : '#bbb', fontSize: '0.7rem' }}>
            v{process.env.REACT_APP_VERSION || require('../../../package.json').version}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      width: '100vw',
      minHeight: '100vh',
      bgcolor: isDarkMode ? '#111827' : '#f8fafc',
      display: 'flex',
      flexDirection: 'row',
      transition: 'background-color 0.3s ease'
    }}>
      <CssBaseline />
      {/* 상단 헤더 */}
      <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', zIndex: 1201 }}>
        <Header onLogout={handleLogout} isDarkMode={isDarkMode} />
      </Box>
      {/* 사이드바 */}
      <Box
        component="nav"
        sx={{
          width: { sm: sidebarCollapsed ? collapsedDrawerWidth + 16 : drawerWidth + 16 },
          flexShrink: { sm: 0 },
          p: 0,
          mt: '110px',
          transition: 'width 0.3s ease-in-out'
        }}
        aria-label="sidebar"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: isDarkMode ? '#1f2937' : '#ffffff',
              border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
              borderRadius: 2,
              boxShadow: isDarkMode ? '0 1px 3px 0 rgba(0, 0, 0, 0.3)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              top: '110px',
              height: 'calc(100vh - 110px)',
              m: 0,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              bgcolor: isDarkMode ? '#1f2937' : '#ffffff',
              border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
              borderRadius: 2,
              boxShadow: isDarkMode ? '0 1px 3px 0 rgba(0, 0, 0, 0.3)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              top: '110px',
              height: 'calc(100vh - 110px)',
              m: 0,
              transition: 'width 0.3s ease-in-out, background-color 0.3s ease, border-color 0.3s ease',
            },
          }}
          open={true}
        >
          {drawer}
        </Drawer>
      </Box>
      {/* 메인 컨텐츠 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 4 },
          width: { sm: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth + 16 : drawerWidth + 16}px)` },
          mt: { xs: 16, sm: 16 },
          bgcolor: isDarkMode ? '#111827' : 'transparent',
          transition: 'width 0.3s ease-in-out, background-color 0.3s ease',
          minHeight: '100vh',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
